/**
 * Pure transforms shared by the HPO Phenotypes section components and
 * mutation hook. No React, no DOM — keeps the components rendering /
 * orchestration code and the hook focused on TanStack glue.
 *
 * Shapes:
 *   - `FeatureInput`        — the FE-side form shape (camelCase, partial
 *                             ISO `dateObserved`).
 *   - `DraftState`          — the inline editor's local string-only state
 *                             (empty strings instead of `null`).
 *   - `LiveFeatureCreateInput` (from `api/client.ts`) — the on-the-wire
 *                             POST/PUT body for `/persons/{guid}/features`.
 *
 * Helpers go input → wire (`liveFieldsFromInput`), feature ↔ draft
 * (`toDraft` / `toInput`), feature → input (`inputFromFeature`,
 * `defaultFeatureInput`), and input → optimistic VM (`toOptimisticFeature`).
 */

import type {
  LiveFeatureCreateInput,
  LiveFeatureObserved,
  LiveFeatureQualifier,
  LiveFeatureQualifierGroup,
} from '@/api/client';

import type {
  FeatureModifier,
  FeatureModifiers,
  FeatureOccurrence,
  FeatureStatus,
  FeatureViewModel,
  PhenotypeModifierKey,
} from '@/features/patient-record/types';

import { MODIFIER_SPECS } from '@/features/patient-record/lib/featureModifiers';

const KNOWN_OCCURRENCE: ReadonlySet<FeatureOccurrence> = new Set<FeatureOccurrence>([
  'PRE_APPOINTMENT',
  'POST_APPOINTMENT',
  'BOTH',
]);

// ---- shared form shape -----------------------------------------------------

export interface FeatureInput {
  /** HPO identifier, e.g. ``HP:0001250``. Empty string means non-standard. */
  readonly hpoId: string;
  readonly label: string;
  readonly status: FeatureStatus;
  readonly onset: string | null;
  /**
   * HPO modifier term id picked for the onset qualifier (e.g. ``HP:0011463``).
   * ``null`` when onset is free text / unset. Round-trips through the wire
   * `featureQualifierGroups[*].termId`. See PTV2-1051 #2.
   */
  readonly onsetTermId: string | null;
  readonly severity: string | null;
  /** HPO modifier term id for severity; see {@link onsetTermId}. */
  readonly severityTermId: string | null;
  /** Extended HPO clinical-modifier qualifiers; see {@link FeatureModifiers}. PTV2-1169. */
  readonly modifiers: FeatureModifiers;
  /** When the feature was observed relative to the appointment, or ``null``. */
  readonly occurrence: FeatureOccurrence | null;
  readonly notes: string | null;
  /** Partial ISO ``YYYY[-MM[-DD]]``. */
  readonly dateObserved: string | null;
}

/** Status-cycle order used by the row's click-to-cycle badge. */
const STATUS_CYCLE: Readonly<Record<FeatureStatus, FeatureStatus>> = {
  present: 'absent',
  absent: 'unknown',
  unknown: 'present',
};

export function nextStatus(current: FeatureStatus): FeatureStatus {
  return STATUS_CYCLE[current];
}

// ---- feature ↔ input -------------------------------------------------------

/** Strip a `FeatureViewModel` into the editable form shape (no metadata). */
export function inputFromFeature(feature: FeatureViewModel): FeatureInput {
  return {
    hpoId: feature.hpoId,
    label: feature.label,
    status: feature.status,
    onset: feature.onset,
    onsetTermId: feature.onsetTermId,
    severity: feature.severity,
    severityTermId: feature.severityTermId,
    modifiers: feature.modifiers,
    occurrence: feature.occurrence,
    notes: feature.notes,
    dateObserved: feature.dateObserved,
  };
}

/**
 * Build the create-payload shape when adding a brand-new phenotype
 * (everything except the term defaults to "no value"). Used by the
 * section's inline add affordance.
 */
export function defaultFeatureInput(term: {
  readonly id: string;
  readonly label: string;
}): FeatureInput {
  return {
    hpoId: term.id,
    label: term.label,
    status: 'present',
    onset: null,
    onsetTermId: null,
    severity: null,
    severityTermId: null,
    modifiers: {},
    occurrence: null,
    notes: null,
    dateObserved: null,
  };
}

// ---- editor draft state ----------------------------------------------------

/**
 * Mutable string-only state held by `FeatureRowEditor`.
 *
 * Onset and severity are tied to the HPO modifier subtree; the picker
 * keeps `<field>Label` (display) and `<field>TermId` (HPO id) in lockstep.
 * A custom (off-catalogue) value leaves the term id empty — preserved as
 * `stringValue` on the wire so legacy readers still surface the label.
 */
export interface DraftState {
  dateObserved: string;
  onsetLabel: string;
  onsetTermId: string;
  severityLabel: string;
  severityTermId: string;
  /**
   * One entry per {@link MODIFIER_SPECS} key. Each holds the picker's label
   * (display) and term id in lockstep; empty `label` means unset. PTV2-1169.
   */
  modifiers: Record<PhenotypeModifierKey, { label: string; termId: string }>;
  /** Empty string when unset; otherwise one of `FeatureOccurrence`. */
  occurrence: string;
  notes: string;
}

/** Build the string-only draft record of modifiers from a view model. */
function toDraftModifiers(
  source: FeatureModifiers,
): DraftState['modifiers'] {
  const out = {} as DraftState['modifiers'];
  for (const { key } of MODIFIER_SPECS) {
    const m = source[key];
    out[key] = { label: m?.label ?? '', termId: m?.termId ?? '' };
  }
  return out;
}

export function toDraft(feature: FeatureViewModel): DraftState {
  return {
    dateObserved: feature.dateObserved ?? '',
    onsetLabel: feature.onset ?? '',
    onsetTermId: feature.onsetTermId ?? '',
    severityLabel: feature.severity ?? '',
    severityTermId: feature.severityTermId ?? '',
    modifiers: toDraftModifiers(feature.modifiers),
    occurrence: feature.occurrence ?? '',
    notes: feature.notes ?? '',
  };
}

export function toInput(
  feature: FeatureViewModel,
  draft: DraftState,
): FeatureInput {
  const trim = (v: string): string | null => {
    const t = v.trim();
    return t.length > 0 ? t : null;
  };
  const nullish = (v: string): string | null => (v.length > 0 ? v : null);
  const modifiers: Record<string, FeatureModifier> = {};
  for (const { key } of MODIFIER_SPECS) {
    const label = trim(draft.modifiers[key].label);
    if (label === null) continue;
    modifiers[key] = { label, termId: nullish(draft.modifiers[key].termId) };
  }
  return {
    hpoId: feature.hpoId,
    label: feature.label,
    status: feature.status,
    onset: trim(draft.onsetLabel),
    onsetTermId: nullish(draft.onsetTermId),
    severity: trim(draft.severityLabel),
    severityTermId: nullish(draft.severityTermId),
    modifiers,
    occurrence: narrowOccurrence(draft.occurrence),
    notes: trim(draft.notes),
    dateObserved: draft.dateObserved.length > 0 ? draft.dateObserved : null,
  };
}

function narrowOccurrence(raw: string): FeatureOccurrence | null {
  if (raw.length === 0) return null;
  return KNOWN_OCCURRENCE.has(raw as FeatureOccurrence)
    ? (raw as FeatureOccurrence)
    : null;
}

// ---- live-payload transforms ----------------------------------------------

const STATUS_TO_OBSERVED: Readonly<Record<FeatureStatus, LiveFeatureObserved>> = {
  present: 'Y',
  absent: 'N',
  unknown: 'U',
};

/**
 * Build the live qualifier-group list from the flat onset / severity /
 * notes fields. Each non-null field becomes a single-qualifier group;
 * missing fields drop out.
 *
 * Every qualifier carries the four wire-required strings (`termId`,
 * `guid`, `label`, `stringValue`) — blank strings for fields the
 * free-text editor doesn't populate. The BE assigns real GUIDs server-
 * side for new qualifiers; we still must serialise the field.
 */
function liveQualifierGroups(input: FeatureInput): LiveFeatureQualifierGroup[] {
  const groups: LiveFeatureQualifierGroup[] = [];
  const push = (
    type: LiveFeatureQualifier['type'],
    value: string,
    termId: string,
  ): void => {
    groups.push({
      // BE assigns a real GUID; empty placeholder for the create wire.
      id: '',
      featureQualifiers: [
        {
          type,
          termId,
          guid: '',
          label: value,
          stringValue: value,
        },
      ],
    });
  };
  if (input.onset !== null && input.onset.length > 0) {
    push('AGE_OF_ONSET', input.onset, input.onsetTermId ?? '');
  }
  if (input.severity !== null && input.severity.length > 0) {
    push('SEVERITY', input.severity, input.severityTermId ?? '');
  }
  for (const { key, wireType } of MODIFIER_SPECS) {
    const m = input.modifiers[key];
    if (m !== undefined && m.label.length > 0) {
      push(wireType, m.label, m.termId ?? '');
    }
  }
  if (input.occurrence !== null) {
    push('OCCURRENCE', input.occurrence, '');
  }
  if (input.notes !== null && input.notes.length > 0) {
    push('NOTES', input.notes, '');
  }
  return groups;
}

/** Partial ISO ``YYYY[-MM[-DD]]`` → live `SimpleDateDTO` triple. */
function toLiveDate(
  iso: string | null,
): { day: number; month: number; year: number } | null {
  if (iso === null || iso.length === 0) return null;
  const parts = iso.split('-');
  const year = parseInt(parts[0] ?? '', 10);
  if (Number.isNaN(year)) return null;
  const month = parts[1] !== undefined ? parseInt(parts[1], 10) : NaN;
  const day = parts[2] !== undefined ? parseInt(parts[2], 10) : NaN;
  return {
    year,
    month: Number.isNaN(month) ? 0 : month,
    day: Number.isNaN(day) ? 0 : day,
  };
}

/**
 * FeatureInput → LiveFeatureCreateInput. The BE rejects payloads with
 * missing sibling arrays (`childrenIds`, `supportingDocuments`,
 * `featureQualifierGroups`) and needs a `source`; we always include
 * defaults. `source.type: 'USER'` distinguishes FE-authored rows from
 * BE-`GENERATED` ones.
 */
export function liveFieldsFromInput(input: FeatureInput): LiveFeatureCreateInput {
  const isStandard = input.hpoId.length > 0;
  return {
    type: 'PHENOTYPE',
    isStandard,
    label: input.label,
    termId: isStandard ? input.hpoId : '',
    observed: STATUS_TO_OBSERVED[input.status],
    parentId: null,
    childrenIds: [],
    supportingDocuments: [],
    dateObserved: toLiveDate(input.dateObserved),
    featureQualifierGroups: liveQualifierGroups(input),
    source: { type: 'USER', label: '' },
  };
}

// ---- optimistic VM ---------------------------------------------------------

/**
 * Build the optimistic `FeatureViewModel` that the mutation hook injects
 * into the query cache while the server round-trips. Hardcodes
 * `kind: 'PHENOTYPE'` because the Phenotypes section is the only entry
 * point that builds inputs today.
 */
export function toOptimisticFeature(
  input: FeatureInput,
  id: string,
): FeatureViewModel {
  return {
    id,
    hpoId: input.hpoId,
    label: input.label,
    status: input.status,
    kind: 'PHENOTYPE',
    onset: input.onset,
    onsetTermId: input.onsetTermId,
    severity: input.severity,
    severityTermId: input.severityTermId,
    modifiers: input.modifiers,
    occurrence: input.occurrence,
    notes: input.notes,
    dateObserved: input.dateObserved,
    createdAt: null,
  };
}
